import { useCallback } from 'react';

import { type BoardCustomFieldValue, CustomFieldTypeName, type PublicBoard } from '@air/api/types';

import { PublicBoardCustomFieldDateInput } from '~/components/PublicBoard/PublicBoardCustomFieldDateInput';
import { PublicBoardCustomFieldSelect } from '~/components/PublicBoard/PublicBoardCustomFieldSelect';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { PublicBoardCustomFieldPlainTextInputTableCell } from '~/components/RightPanel/CustomFields/PublicBoardCustomFieldPlainTextInputTableCell';
import { BaseCustomFieldTableCell } from '~/components/TableView/TableRow/CustomFieldTableCell/BaseCustomFieldTableCell';
import { type BoardCustomFieldTableCellProps } from '~/components/TableView/TableRow/CustomFieldTableCell/types';
import { usePublicCustomFieldsContext } from '~/providers/PublicCustomFieldsProvider';
import { type AssignedCustomFieldValue } from '~/swr-hooks/customFields/types';
import { canChangeBoardCustomFields } from '~/utils/permissions/boardPermissions';

export const PublicBoardCustomFieldTableCell = ({
  board,
  customFieldName,
  customFieldId,
}: BoardCustomFieldTableCellProps<PublicBoard>) => {
  const actualCustomFieldValue = board.customFields?.find((customField) => customField.id === customFieldId);
  // biome-ignore lint/plugin: the public board custom field is narrowed to the assigned value shape used by the cell renderer
  const assignedCustomFieldValue = actualCustomFieldValue as AssignedCustomFieldValue | undefined;

  const { permissions } = usePublicPermissions();
  const canEditCustomFields = canChangeBoardCustomFields(permissions);
  const { customFields } = usePublicCustomFieldsContext();
  const customField = customFields?.find((field) => field.id === customFieldId);

  const renderEditableCustomField = useCallback(
    (closeEditor: () => void) => {
      if (!customField) {
        return null;
      }
      if (!canEditCustomFields) {
        return null;
      }
      const boardCustomFieldValue: BoardCustomFieldValue = actualCustomFieldValue || {
        ...customField,
        boardId: board.id,
        value: null,
        values: null,
      };

      switch (customField.type.title) {
        case CustomFieldTypeName.singleSelect:
        case CustomFieldTypeName.multiSelect: {
          return (
            <PublicBoardCustomFieldSelect
              autoFocus
              boardId={board.id}
              canEditCustomFields={canEditCustomFields}
              onOptionsClose={closeEditor}
              field={boardCustomFieldValue}
              trackingLocation="table-cell"
              className="ads:bg-base bg-gray-1"
            />
          );
        }
        case CustomFieldTypeName.plainText: {
          return (
            <PublicBoardCustomFieldPlainTextInputTableCell
              boardId={board.id}
              closeEditor={closeEditor}
              field={boardCustomFieldValue}
              trackingLocation="table-cell"
            />
          );
        }
        case CustomFieldTypeName.date: {
          return (
            <PublicBoardCustomFieldDateInput
              isEditing
              boardId={board.id}
              canEditCustomFields={canEditCustomFields}
              field={boardCustomFieldValue}
              onBlur={closeEditor}
              trackingLocation="table-cell"
            />
          );
        }
      }
    },
    [actualCustomFieldValue, board.id, canEditCustomFields, customField],
  );

  return (
    <BaseCustomFieldTableCell
      customField={customField}
      customFieldId={customFieldId}
      customFieldName={customFieldName}
      assignedCustomFieldValue={assignedCustomFieldValue}
      renderEditableCustomField={canEditCustomFields ? renderEditableCustomField : undefined}
    />
  );
};
